import { HttpClient } from '@angular/common/http';
import {
  Component,
  effect,
  ElementRef,
  HostListener,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { LucideAngularModule } from 'lucide-angular';
import { environment } from '../../../../environments/environment';
import {
  fileAttachmentDisplayName,
  parseFileFieldValueToAttachments,
  type FileFieldAttachment,
} from '../../utils';
import { TPipe } from '../../pipes/t.pipe';

@Component({
  selector: 'app-list-file-field-cell',
  standalone: true,
  imports: [LucideAngularModule, TPipe],
  template: `
    <div class="relative inline-flex min-w-0 max-w-full">
      <button
        #triggerBtn
        type="button"
        class="h-8 w-8 shrink-0 rounded-[var(--r-md)] border border-[var(--border)] bg-[var(--bg-surface)] inline-flex items-center justify-center text-[var(--muted)] hover:bg-[var(--hover)] hover:text-[var(--text)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)]"
        [class.ring-2]="menuOpen()"
        [class.ring-[var(--focus)]]="menuOpen()"
        [attr.aria-expanded]="menuOpen()"
        [attr.aria-label]="'groupedList.fileField.openMenuAria' | t"
        (click)="onTriggerClick($event)"
      >
        <lucide-icon
          name="paperclip"
          class="h-4 w-4"
          [class.text-[var(--active-text)]]="hasAttachments()"
          [class.text-[var(--muted)]]="!hasAttachments()"
        ></lucide-icon>
      </button>

      @if (menuOpen() && fixedGeom(); as g) {
        <div
          class="py-2 rounded-xl border border-[var(--border)] bg-[var(--bg-surface)] shadow-lg"
          role="menu"
          [style.position]="'fixed'"
          [style.top.px]="g.top"
          [style.left.px]="g.left"
          [style.width.px]="g.width"
          [style.z-index]="6000"
          (mousedown)="$event.stopPropagation()"
          (click)="$event.stopPropagation()"
        >
          @if (attachments().length === 0) {
            <p class="text-xs text-[var(--muted)] px-3 pb-2">
              {{ 'groupedList.fileField.empty' | t }}
            </p>
          } @else {
            <p class="text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] px-3 pb-1">
              {{ 'groupedList.fileField.filesHeading' | t }}
            </p>
            <ul class="max-h-44 overflow-y-auto px-1" role="none">
              @for (a of attachments(); track trackAtt(a, $index)) {
                <li role="none">
                  <button
                    type="button"
                    role="menuitem"
                    class="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-left text-sm text-[var(--text)] hover:bg-[var(--hover)] min-w-0"
                    (click)="onDownloadClick(a)"
                  >
                    <lucide-icon name="download" class="h-4 w-4 shrink-0 text-[var(--muted)]"></lucide-icon>
                    <span class="truncate min-w-0">{{ displayName(a) }}</span>
                  </button>
                </li>
              }
            </ul>
          }
          <div class="border-t border-[var(--border)] mt-1 pt-2 px-2">
            <label
              class="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-[var(--hover)] cursor-pointer text-sm font-medium text-[var(--text)] min-w-0"
            >
              <lucide-icon name="plus" class="h-4 w-4 shrink-0 text-[var(--muted)]"></lucide-icon>
              <span class="truncate">{{ 'groupedList.fileField.addFile' | t }}</span>
              <input
                type="file"
                class="sr-only"
                [accept]="acceptedTypes"
                (change)="onFileChosen($event)"
              />
            </label>
          </div>
        </div>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: inline-flex;
        max-width: 100%;
      }
    `,
  ],
})
export class ListFileFieldCellComponent {
  private readonly http = inject(HttpClient);
  private readonly host = inject(ElementRef<HTMLElement>);

  readonly triggerRef = viewChild<ElementRef<HTMLButtonElement>>('triggerBtn');

  readonly cardId = input.required<string>();
  readonly rawValue = input<string | null>(null);

  readonly fileSelected = output<File>();

  readonly menuOpen = signal(false);
  readonly fixedGeom = signal<{ top: number; left: number; width: number } | null>(null);
  private scrollCleanups: Array<() => void> = [];

  readonly acceptedTypes = '.pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.csv,.txt';

  readonly attachments = computed(() => parseFileFieldValueToAttachments(this.rawValue() ?? ''));

  readonly hasAttachments = computed(() => this.attachments().length > 0);

  constructor() {
    effect((onCleanup) => {
      if (!this.menuOpen()) {
        this.fixedGeom.set(null);
        this.unbindScroll();
        return;
      }
      queueMicrotask(() => {
        if (!this.menuOpen()) return;
        this.syncFixedGeom();
        this.bindScroll();
      });
      onCleanup(() => {
        this.unbindScroll();
        this.fixedGeom.set(null);
      });
    });
  }

  trackAtt(a: FileFieldAttachment, index: number): string {
    return `${a.id ?? ''}-${a.storageKey ?? ''}-${a.url ?? ''}-${index}`;
  }

  displayName(a: FileFieldAttachment): string {
    return fileAttachmentDisplayName(a) || '—';
  }

  onTriggerClick(ev: Event): void {
    ev.preventDefault();
    ev.stopPropagation();
    this.menuOpen.update((v) => !v);
  }

  closeMenu(): void {
    this.menuOpen.set(false);
  }

  onFileChosen(ev: Event): void {
    const inputEl = ev.target as HTMLInputElement;
    const file = inputEl.files?.[0];
    inputEl.value = '';
    if (!file) return;
    this.fileSelected.emit(file);
    this.closeMenu();
  }

  async onDownloadClick(entry: FileFieldAttachment): Promise<void> {
    const name = fileAttachmentDisplayName(entry) || 'download';
    const ok = await this.fetchAsBlob(entry, (blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.rel = 'noopener';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 120_000);
    });
    if (!ok) {
      const u = this.resolveDirectUrl(entry);
      if (u) {
        const a = document.createElement('a');
        a.href = u;
        a.download = name;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.click();
      }
    }
    this.closeMenu();
  }

  @HostListener('document:pointerdown', ['$event'])
  onDocumentPointerDown(ev: PointerEvent): void {
    if (!this.menuOpen()) return;
    const root = this.host.nativeElement;
    if (!root.contains(ev.target as Node)) {
      this.closeMenu();
    }
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(ev: KeyboardEvent): void {
    if (ev.key === 'Escape' && this.menuOpen()) {
      ev.stopPropagation();
      this.closeMenu();
    }
  }

  private syncFixedGeom(): void {
    const btn = this.triggerRef()?.nativeElement;
    if (!(btn instanceof HTMLElement)) return;
    const r = btn.getBoundingClientRect();
    this.fixedGeom.set({
      top: Math.round(r.bottom + 6),
      left: Math.round(r.left),
      width: Math.max(240, Math.round(r.width)),
    });
  }

  private bindScroll(): void {
    this.unbindScroll();
    const sync = () => this.syncFixedGeom();
    const onResize = () => sync();
    window.addEventListener('resize', onResize);
    this.scrollCleanups.push(() => window.removeEventListener('resize', onResize));

    let el: HTMLElement | null = this.host.nativeElement;
    while (el) {
      const st = getComputedStyle(el);
      const scrollY = /(auto|scroll|overlay)/.test(st.overflowY) && el.scrollHeight > el.clientHeight;
      const scrollX = /(auto|scroll|overlay)/.test(st.overflowX) && el.scrollWidth > el.clientWidth;
      if (scrollY || scrollX) {
        el.addEventListener('scroll', sync, { passive: true });
        const target = el;
        this.scrollCleanups.push(() => target.removeEventListener('scroll', sync));
      }
      el = el.parentElement;
    }
  }

  private unbindScroll(): void {
    for (const c of this.scrollCleanups) c();
    this.scrollCleanups = [];
  }

  private async fetchAsBlob(
    entry: FileFieldAttachment,
    useBlob: (b: Blob) => void
  ): Promise<boolean> {
    const direct = this.resolveDirectUrl(entry);
    if (direct) {
      try {
        const blob = await firstValueFrom(this.http.get(direct, { responseType: 'blob' }));
        useBlob(blob);
        return true;
      } catch {
        /* tenta por id */
      }
    }
    const cardId = this.cardId();
    const id = entry.id?.trim();
    if (cardId && id) {
      const base = environment.apiUrl.replace(/\/$/, '');
      const downloadUrl = `${base}/api/cards/${cardId}/attachments/${id}/download`;
      try {
        const blob = await firstValueFrom(this.http.get(downloadUrl, { responseType: 'blob' }));
        useBlob(blob);
        return true;
      } catch {
        return false;
      }
    }
    return false;
  }

  private resolveDirectUrl(entry: FileFieldAttachment): string | null {
    const raw = (entry.url || entry.assetUrl || entry.fileUrl || entry.path || '').trim();
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw) || raw.startsWith('data:') || raw.startsWith('blob:')) return raw;
    const base = environment.apiUrl.replace(/\/$/, '');
    const normalized = raw.replace(/\\/g, '/');
    if (normalized.startsWith('/')) return `${base}${normalized}`;
    return `${base}/${normalized}`;
  }
}
